import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { RoleGuard } from './auth/RoleGuard';
import { LoansPage } from './pages/LoansPage';
import { LoanDetailPage } from './pages/LoanDetailPage';
import { ReportsPage } from './pages/ReportsPage';
import { ImportPage } from './pages/ImportPage';
import { VersionFooter } from './components/VersionFooter';
import { ROLE_ADMIN, ROLE_LOAN_ANALYST, ROLE_SPECIAL_ASSETS } from '@app/shared';

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <Link to={to} className={`nav-link${pathname.startsWith(to) ? ' active' : ''}`}>
      {children}
    </Link>
  );
}

/**
 * Non-production role/identity picker for UAT. Renders nothing unless the API
 * advertises a roster (IMPERSONATION_ENABLED), so it strips itself in prod.
 *
 * Switching swaps identity as well as role — the seeded fixture users own the
 * loans, requests, and assignments in the test data, so an impersonated SA rep
 * gets a populated work queue instead of an empty one.
 */
function ImpersonationPicker() {
  const { impersonationProfiles, impersonating, impersonate, me } = useAuth();
  if (impersonationProfiles.length === 0) return null;

  return (
    <div className="impersonation-picker" title="Testing aid — not present in production">
      <label htmlFor="impersonate-select">Acting as</label>
      <select
        id="impersonate-select"
        value={impersonating ?? ''}
        onChange={e => void impersonate(e.target.value || null)}
      >
        <option value="">Myself ({me?.impersonatedBy?.displayName ?? me?.displayName})</option>
        {impersonationProfiles.map(p => (
          <option key={p.key} value={p.key}>
            {p.displayName} — {p.roles.join(', ')}
          </option>
        ))}
      </select>
    </div>
  );
}

export function App() {
  const { me, loading, login, logout, authError, switchAccount, hasRole, impersonating } = useAuth();

  const isAdmin = hasRole(ROLE_ADMIN);
  const isAnalyst = hasRole(ROLE_LOAN_ANALYST);
  const isSA = hasRole(ROLE_SPECIAL_ASSETS);
  // Loans are now visible to everyone; Special Assets reps use the list to find
  // a loan and submit a request from the detail page.
  const canViewLoans = isAdmin || isAnalyst || isSA;
  const canImport = isAdmin;
  // Admin only, deliberately: the exports are the whole portfolio, unfiltered, including
  // every note — which is more than an analyst's or officer's own queue.
  const canViewReports = isAdmin;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-brand">
          <span className="brand-name">UMWSB</span>
          <span className="brand-sub">Purchase &amp; Liquidation Database</span>
        </div>

        <nav className="app-nav">
          {me && (
            <>
              {canViewLoans && <NavLink to="/loans">Loans</NavLink>}
              {canViewReports && <NavLink to="/reports">Reports</NavLink>}
              {canImport && <NavLink to="/import">Import</NavLink>}
            </>
          )}
        </nav>

        <div className="header-auth">
          {loading ? null : me ? (
            <div className="user-menu">
              <ImpersonationPicker />
              <div className="user-info">
                <span className="user-name">{me.displayName ?? me.email}</span>
                <span className="user-roles">{me.roles.join(', ')}</span>
              </div>
              <button className="btn-sm" onClick={() => void logout()}>Sign out</button>
            </div>
          ) : (
            <button className="btn-primary" onClick={() => void login()}>Sign in</button>
          )}
        </div>
      </header>

      <main className="app-main">
        {loading ? (
          <p className="loading">Loading…</p>
        ) : !me ? (
          <div className="sign-in-prompt">
            <h2>Purchase &amp; Liquidation Database</h2>
            <p>Sign in with your UMWSB account to continue.</p>
            {/* A rejected account is the common cause of an apparent bounce back to
                this screen: the sign-in itself worked, the API refused the token.
                Say so, and offer the account picker rather than the same button. */}
            {authError && (
              <div className="sign-in-error" role="alert">
                <strong>Sign-in did not complete.</strong>
                <p>{authError}</p>
              </div>
            )}
            <button className="btn-primary btn-lg" onClick={() => void login()}>Sign in with Microsoft</button>
            {authError && (
              <button className="btn-sm" onClick={() => void switchAccount()}>Use a different account</button>
            )}
          </div>
        ) : (
          // Keyed on the impersonated identity: pages fetch in their own
          // effects and hold the previous user's rows in local state, so
          // switching identity has to remount the tree to force a clean refetch.
          <Routes key={impersonating ?? 'self'}>
            <Route path="/" element={canViewLoans ? <LoansPage /> : <p>Access denied.</p>} />
            <Route path="/loans" element={<LoansPage />} />
            <Route path="/loans/:id" element={<LoanDetailPage />} />
            <Route path="/reports" element={
              <RoleGuard role={ROLE_ADMIN}>
                <ReportsPage />
              </RoleGuard>
            } />
            <Route path="/import" element={
              <RoleGuard role={ROLE_ADMIN}>
                <ImportPage />
              </RoleGuard>
            } />
            <Route path="*" element={<p className="not-found">Page not found.</p>} />
          </Routes>
        )}
      </main>

      <VersionFooter />
    </div>
  );
}
